import { useEffect, useMemo, useReducer, useRef } from 'react';

import { useDashboardAccountScope } from '../voters/voterAccountScope';
import { isCanvassingSessionId } from './useCanvassingSessionDetail';
import {
  classifyMapOverlayError,
  fetchCanvassingSessionDoorVisitMap,
  fetchVotersInBounds,
  isAbortError,
  MAP_OVERLAY_PAGE_SIZE,
  type MapOverlayFailure,
} from './canvassingSessionMapOverlayApi';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';
import {
  type MapOverlayBounds,
  type MapOverlayPage,
  type MapOverlayVisit,
  type MapOverlayVoter,
} from './canvassingSessionMapOverlay';
import {
  createMapOverlayModel,
  reduceMapOverlay,
  selectOverlayRequestPlan,
  selectRenderedOverlay,
  type MapOverlayModel,
  type OverlayRequest,
} from './canvassingSessionMapOverlayModel';

export type CanvassingSessionMapOverlayApi = {
  fetchVoters: (
    request: { bounds: MapOverlayBounds; first: number; after?: string },
    options: { signal: AbortSignal },
  ) => Promise<MapOverlayPage<MapOverlayVoter>>;
  fetchVisits: (
    request: { sessionId: string; first: number; after?: string },
    options: { signal: AbortSignal },
  ) => Promise<MapOverlayPage<MapOverlayVisit>>;
};

export type UseCanvassingSessionMapOverlayOptions = {
  sessionId: string | undefined;
  isStructurelyAdmin: boolean;
  serverBounds: MapOverlayBounds | null;
  boundsRevision: number;
  gpsSettled: boolean;
  gpsRefreshing: boolean;
  gpsFailed: boolean;
  fetchImpl?: CanvassingSessionFetchImpl;
  api?: CanvassingSessionMapOverlayApi;
};

export type UseCanvassingSessionMapOverlayResult = {
  showVotersToggle: boolean;
  votersToggle: boolean;
  visitsToggle: boolean;
  setVotersToggle: (enabled: boolean) => void;
  setVisitsToggle: (enabled: boolean) => void;
  refresh: () => void;
  retryVoters: () => void;
  retryVisits: () => void;
  model: MapOverlayModel;
  renderVoters: boolean;
  renderVisits: boolean;
  voters: MapOverlayVoter[];
  visits: MapOverlayVisit[];
  initialSettled: boolean;
  votersLoading: boolean;
  visitsLoading: boolean;
  refreshing: boolean;
};

const overlayApiFromFetch = (fetchImpl: CanvassingSessionFetchImpl | undefined): CanvassingSessionMapOverlayApi => ({
  fetchVoters: (request, { signal }) => fetchVotersInBounds({ ...request, signal, fetchImpl }),
  fetchVisits: (request, { signal }) => fetchCanvassingSessionDoorVisitMap({ ...request, signal, fetchImpl }),
});

const requestKey = (request: OverlayRequest | null, bounds: MapOverlayBounds | null = null): string => {
  if (request === null) return '';
  const boundKey = bounds === null ? '' : `${bounds.north},${bounds.south},${bounds.east},${bounds.west}`;
  return `${request.generation}:${request.epoch}:${request.after ?? ''}:${request.restart}:${boundKey}`;
};

export const useCanvassingSessionMapOverlay = ({
  sessionId,
  isStructurelyAdmin,
  serverBounds,
  boundsRevision,
  gpsSettled,
  gpsRefreshing,
  gpsFailed,
  fetchImpl,
  api: apiOverride,
}: UseCanvassingSessionMapOverlayOptions): UseCanvassingSessionMapOverlayResult => {
  const accountScope = useDashboardAccountScope();
  const api = useMemo(
    () => apiOverride ?? overlayApiFromFetch(fetchImpl),
    [apiOverride, fetchImpl],
  );
  const [model, dispatch] = useReducer(reduceMapOverlay, isStructurelyAdmin, createMapOverlayModel);
  const sessionRef = useRef(sessionId);
  const adminRef = useRef(isStructurelyAdmin);
  sessionRef.current = sessionId;
  adminRef.current = isStructurelyAdmin;

  useEffect(() => {
    dispatch({ type: 'reset', admin: adminRef.current });
  }, [sessionId, accountScope]);

  useEffect(() => {
    dispatch({ type: 'permission', admin: isStructurelyAdmin });
  }, [isStructurelyAdmin]);

  useEffect(() => {
    dispatch({
      type: 'bounds',
      revision: boundsRevision,
      bounds: serverBounds,
      settled: gpsSettled,
      refreshing: gpsRefreshing,
      failed: gpsFailed,
    });
  }, [boundsRevision, failedBoundsKey(serverBounds), gpsFailed, gpsRefreshing, gpsSettled, serverBounds]);

  const plan = selectOverlayRequestPlan(model);
  const visitKey = requestKey(plan.visits);
  const voterKey = requestKey(plan.voters, plan.voters?.bounds ?? null);

  useEffect(() => {
    if (!isCanvassingSessionId(sessionId) || plan.visits === null) return undefined;
    const request = plan.visits;
    const controller = new AbortController();
    void api.fetchVisits({
      sessionId,
      first: MAP_OVERLAY_PAGE_SIZE,
      ...(request.after === null ? {} : { after: request.after }),
    }, { signal: controller.signal }).then(page => {
      if (controller.signal.aborted || sessionRef.current !== sessionId) return;
      dispatch({
        type: 'visits-page',
        generation: request.generation,
        epoch: request.epoch,
        items: page.items,
        hasNextPage: page.hasNextPage,
        endCursor: page.endCursor,
        restart: request.restart,
      });
    }).catch((error: unknown) => {
      if (controller.signal.aborted || isAbortError(error) || sessionRef.current !== sessionId) return;
      dispatch({
        type: 'visits-failure',
        generation: request.generation,
        epoch: request.epoch,
        error: classifyMapOverlayError(error, 'visits'),
      });
    });
    return () => controller.abort();
  }, [api, sessionId, visitKey]);

  useEffect(() => {
    if (!isCanvassingSessionId(sessionId) || !isStructurelyAdmin || plan.voters === null) return undefined;
    const request = plan.voters;
    const controller = new AbortController();
    void api.fetchVoters({
      bounds: request.bounds,
      first: MAP_OVERLAY_PAGE_SIZE,
      ...(request.after === null ? {} : { after: request.after }),
    }, { signal: controller.signal }).then(page => {
      if (controller.signal.aborted || sessionRef.current !== sessionId) return;
      dispatch({
        type: 'voters-page',
        generation: request.generation,
        epoch: request.epoch,
        items: page.items,
        hasNextPage: page.hasNextPage,
        endCursor: page.endCursor,
        restart: request.restart,
      });
    }).catch((error: unknown) => {
      if (controller.signal.aborted || isAbortError(error) || sessionRef.current !== sessionId) return;
      const failure: MapOverlayFailure = classifyMapOverlayError(error, 'voters');
      dispatch({
        type: 'voters-failure',
        generation: request.generation,
        epoch: request.epoch,
        error: failure,
      });
    });
    return () => controller.abort();
  }, [api, isStructurelyAdmin, sessionId, voterKey]);

  const rendered = selectRenderedOverlay(model);
  return {
    showVotersToggle: model.admin,
    votersToggle: model.votersToggle,
    visitsToggle: model.visitsToggle,
    setVotersToggle: enabled => dispatch({ type: 'toggle-voters', enabled }),
    setVisitsToggle: enabled => dispatch({ type: 'toggle-visits', enabled }),
    refresh: () => dispatch({ type: 'refresh' }),
    retryVoters: () => dispatch({ type: 'retry-voters' }),
    retryVisits: () => dispatch({ type: 'retry-visits' }),
    model,
    renderVoters: rendered.renderVoters,
    renderVisits: rendered.renderVisits,
    voters: rendered.voters,
    visits: rendered.visits,
    initialSettled: model.initialSettled,
    votersLoading: plan.voters !== null,
    visitsLoading: plan.visits !== null,
    refreshing: model.incoming !== null || model.awaitingFreshBounds,
  };
};

const failedBoundsKey = (bounds: MapOverlayBounds | null): string =>
  bounds === null ? 'none' : `${bounds.north},${bounds.south},${bounds.east},${bounds.west}`;
